import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getDashboardStats,
} from "../../features/admin/adminSlice";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import {
  FaCar,
  FaUser,
  FaCalendarAlt,
  FaRupeeSign,
  FaChartLine,
  FaFilter,
  FaSearch,
  FaTaxi,
  FaUserTie,
  FaListAlt,
  FaBuilding,
} from "react-icons/fa";
import RecentTransactionsTable from "../../components/Admin/RecentTransactionsTable";
import NewDriversList from "../../components/Admin/NewDriversList";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { dashboardStats, isLoading } =
    useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);

  const [period, setPeriod] = useState("weekly");
  const [bookingFilter, setBookingFilter] = useState("all");

  const [searchQuery, setSearchQuery] = useState("");


  // Prevent multiple API calls with a ref
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && !isLoading) {
      hasInitialized.current = true;
      dispatch(getDashboardStats());
    }
  }, [dispatch, isLoading, user]);

  // Fetch assigned_to_vendor bookings count from the bookings API (reflects /admin/bookings?status=assigned_to_vendor)
  const [assignedVendorCount, setAssignedVendorCount] = useState(null);

  useEffect(() => {
    const fetchAssignedVendorCount = async () => {
      try {
        const API_URL = BASE_API_URL;
        let config = {};
        if (user && user.token) {
          config = { headers: { Authorization: `Bearer ${user.token}` } };
        }
        const res = await axios.get(`${API_URL}/booking/admin/all`, config);
        const allBookings = (res.data && res.data.data) || [];
        const count = allBookings.filter(b => b.status === 'assigned_to_vendor').length;
        setAssignedVendorCount(count);
      } catch (err) {
        console.warn("Failed to fetch assigned_to_vendor bookings:", err?.message || err);
        setAssignedVendorCount(0);
      }
    };

    fetchAssignedVendorCount();
  }, [user, dispatch, dashboardStats]);


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">Loading dashboard data...</span>
      </div>
    );
  }

  // If stats are not yet loaded, show placeholders
  const stats = dashboardStats || {
    totalUsers: 0,
    totalBookings: 0,
    totalCabs: 0,
    recentBookings: [],
    monthlyRevenueChart: [],
    totalRevenue: 0,
    pendingBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    todayBookings: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
  };


  return (
    <div className="container mx-auto px-2 py-0">
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
          Admin Dashboard
        </h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        </div>
      </div>

      {/* Quick Access Links removed as per request */}

      {/* Row 1: Top summary (Total Users, Total Drivers, Total Vendors) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Link to="/admin/users" className="block">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-800">
                <FaUser className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <h2 className="text-gray-600">Total Users</h2>
                <p className="text-2xl font-bold">{stats.totalUsers || 0}</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/admin/drivers" className="block">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-800">
                <FaUserTie className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <h2 className="text-gray-600">Total Drivers</h2>
                <p className="text-2xl font-bold">{stats.totalDrivers || 0}</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/admin/vendors" className="block">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-800">
                <FaBuilding className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <h2 className="text-gray-600">Total Vendors</h2>
                <p className="text-2xl font-bold">{stats.totalVendors || 0}</p>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Row 2: Booking status overview (Pending, Assigned to Driver, Assigned to Vendor) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Link to="/admin/bookings?status=pending" className="block">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-gray-500 text-sm">Pending Bookings</h3>
                <p className="text-2xl font-bold">{stats.pendingBookings || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-800">
                <FaCalendarAlt className="w-6 h-6" />
              </div>
            </div>
          </div>
        </Link>

        <Link to="/admin/bookings?status=assigned" className="block">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-gray-500 text-sm">Assigned Booking To Driver</h3>
                <p className="text-2xl font-bold">{stats.assignedToDriver || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-indigo-100 text-indigo-800">
                <FaTaxi className="w-6 h-6" />
              </div>
            </div>
          </div>
        </Link>

        <Link to="/admin/bookings?status=assigned_to_vendor" className="block">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-gray-500 text-sm">Assigned Booking To Vendor</h3>
                <p className="text-2xl font-bold">{assignedVendorCount !== null ? assignedVendorCount : (stats.assignedToVendor || 0)}</p>
              </div>
              <div className="p-3 rounded-full bg-red-100 text-red-800">
                <FaListAlt className="w-6 h-6" />
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Row 3: Earnings (Today's, Weekly, Total admin commission earnings) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
          <h3 className="text-gray-500 text-sm">Today's Earning</h3>
          <p className="text-2xl font-bold">₹{stats.todayAdminEarnings || 0}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
          <h3 className="text-gray-500 text-sm">Weekly Earning</h3>
          <p className="text-2xl font-bold">₹{stats.weeklyAdminEarnings || 0}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
          <h3 className="text-gray-500 text-sm">Total Earning</h3>
          <p className="text-2xl font-bold">₹{stats.totalAdminEarnings || 0}</p>
        </div>
      </div>

      {/* Booking filters */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent Bookings</h2>

          <div className="flex flex-col md:flex-row gap-2 mt-2 md:mt-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search bookings..."
                className="pl-8 pr-4 py-1 border border-gray-300 rounded text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FaSearch className="absolute left-2 top-2 text-gray-400" />
            </div>

            <select
              className="admin-dropdown"
              value={bookingFilter}
              onChange={(e) => setBookingFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-3 text-left">Booking ID</th>
                <th className="py-2 px-3 text-left">User</th>
                <th className="py-2 px-3 text-left">Phone</th>
                <th className="py-2 px-3 text-left">Route</th>
                <th className="py-2 px-3 text-left">Date</th>
                <th className="py-2 px-3 text-left">Time</th>
                <th className="py-2 px-3 text-left">Service</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Amount</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentBookings && stats.recentBookings.length > 0 ? (
                stats.recentBookings
                  .filter((booking) => {
                    // Apply status filter
                    if (
                      bookingFilter !== "all" &&
                      booking.status !== bookingFilter
                    ) {
                      return false;
                    }

                    // Apply search query
                    if (searchQuery) {
                      const searchLower = searchQuery.toLowerCase();
                      const idMatch =
                        booking._id &&
                        booking._id.toLowerCase().includes(searchLower);
                      const bookingIdMatch =
                        booking.bookingId &&
                        booking.bookingId.toLowerCase().includes(searchLower);
                      const userMatch =
                        booking.user?.name &&
                        booking.user.name.toLowerCase().includes(searchLower);
                      const userNameMatch =
                        booking.userName &&
                        booking.userName.toLowerCase().includes(searchLower);
                      const fromMatch = (
                        booking.from ||
                        booking.pickupLocation ||
                        booking.fromCity ||
                        ""
                      )
                        .toLowerCase()
                        .includes(searchLower);
                      const toMatch = (
                        booking.to ||
                        booking.dropLocation ||
                        booking.toCity ||
                        ""
                      )
                        .toLowerCase()
                        .includes(searchLower);
                      const phoneMatch = (
                        booking.user?.phone || booking.userPhone || booking.passengerDetails?.phone || ""
                      )
                        .toString()
                        .toLowerCase()
                        .includes(searchLower);
                      const serviceMatch = (
                        booking.serviceType || booking.journeyType || ""
                      )
                        .toString()
                        .toLowerCase()
                        .includes(searchLower);

                      return (
                        idMatch ||
                        bookingIdMatch ||
                        userMatch ||
                        userNameMatch ||
                        phoneMatch ||
                        serviceMatch ||
                        fromMatch ||
                        toMatch
                      );
                    }

                    return true;
                  })
                  .map((booking) => {
                    const displayIdRaw = booking.bookingId || (booking._id ? booking._id.slice(-6).toUpperCase() : "N/A");
                    const displayId = displayIdRaw && typeof displayIdRaw === 'string' && displayIdRaw.startsWith('#') ? displayIdRaw : `#${displayIdRaw}`;
                    const displayDate = booking.date
                      ? new Date(booking.date)
                      : booking.pickupDate
                        ? new Date(booking.pickupDate)
                        : booking.travelDate
                          ? new Date(booking.travelDate)
                          : null;
                    const displayTime = booking.pickupTime || (displayDate ? displayDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "");
                    const serviceRaw = booking.serviceType || booking.journeyType || "";
                    const serviceLabel = typeof serviceRaw === 'string' ? serviceRaw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
                    const phone = booking.user?.phone || booking.userPhone || booking.passengerDetails?.phone || '';

                    return (
                      <tr
                        key={booking._id || booking.bookingId}
                        className="border-t"
                      >
                        <td className="py-2 px-3">{displayId}</td>
                        <td className="py-2 px-3">{booking.user?.name || booking.userName || ""}</td>
                        <td className="py-2 px-3">{phone}</td>
                        <td className="py-2 px-3">
                          {booking.from ||
                            booking.pickupLocation ||
                            booking.fromCity ||
                            ""}{" "}
                          to{" "}
                          {booking.to ||
                            booking.dropLocation ||
                            booking.toCity ||
                            ""}
                        </td>
                        <td className="py-2 px-3">{displayDate ? formatDateDDMMYYYY(displayDate) : ""}</td>
                        <td className="py-2 px-3">{displayTime}</td>
                        <td className="py-2 px-3">{serviceLabel}</td>
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              booking.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : booking.status === "cancelled"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {booking.status.charAt(0).toUpperCase() +
                              booking.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-green-600 font-semibold">
                            ₹
                            {booking.amount ||
                              booking.totalAmount ||
                              booking.fare ||
                              0}
                          </span>
                        </td>
                      </tr>
                    );
                  })
              ) : searchQuery || bookingFilter !== "all" ? (
                <tr>
                  <td
                    className="py-4 px-3 text-center text-gray-500"
                    colSpan="9"
                  >
                    No bookings match your filters (Filter: {bookingFilter}, Search: "{searchQuery}", Total Available: {stats.recentBookings?.length || 0})
                  </td>
                </tr>
              ) : (
                <tr>
                  <td
                    className="py-4 px-3 text-center text-gray-500"
                    colSpan="9"
                  >
                    No recent bookings available (Database may be empty or API not returning data)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Recent Wallet Transactions - Temporarily disabled to prevent infinite loops */}
      {/* <RecentTransactionsTable
        transactions={walletTransactions || []}
        isLoading={isLoading}
        onLoadMore={() => {
          // Implementation for load more functionality
        }}
        hasMore={false}
      /> */}

      {/* New Driver Registrations - Temporarily disabled to prevent infinite loops */}
      {/* <div className="mt-8">
        <NewDriversList />
      </div> */}

    </div>
  );
};

export default Dashboard;
